import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
    getFirestore, 
    collection, 
    getDocs, 
    doc, 
    getDoc, 
    query, 
    where,
    updateDoc,
    setDoc,
    addDoc,
    serverTimestamp                       // 👈 add this
  } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import topics from "../../../data/ca-topics/ca-4th-educator-math-topics";
import { markProgress } from "../../../utils/markProgress";
import MusicTool from "../../../components/tools/MusicTool";
import VideoTool from "../../../components/tools/VideoTool";

// ---- Parsing helpers (paste-friendly) ----
function parseFacts(input) {
  return input
    .split(/\r?\n/)
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => s.replace(/^[-–•\d\)\(.\s]+/, "")); // strip bullets/numbers
}

function parseQA(input) {
  const lines = input.split(/\r?\n/);
  const qa = [];
  let i = 0;

  const isQuestion = l =>
    /\?$/.test(l.trim()) ||
    /^(who|what|when|where|why|how)\b/i.test(l.trim());

  while (i < lines.length) {
    let line = lines[i]?.trim() || "";
    if (!line) { i++; continue; }

    if (isQuestion(line)) {
      const question = line.replace(/^\d+[\).\s-]*/, "").trim();
      i++;

      const choices = [];
      let correctIndex = -1;

      while (i < lines.length && lines[i].trim()) {
        let a = lines[i].trim();
        const marked = /^\*/.test(a) || /\(correct\)/i.test(a) || /✓/.test(a);
        a = a.replace(/^(\*|\-|\d+[\)\.]|[a-d][\)\.]|✓)/i, "").trim();
        a = a.replace(/\(correct\)/ig, "").trim();
        choices.push(a);
        if (marked && correctIndex === -1) correctIndex = choices.length - 1;

        // stop if next line looks like a question
        if (i + 1 < lines.length && isQuestion(lines[i + 1])) break;
        i++;
      }

      if (choices.length === 0) {
        // fallback True/False
        choices.push("True", "False");
        correctIndex = question.toLowerCase().includes("(true)") ? 0 : 1;
      }
      if (correctIndex === -1) correctIndex = 0;

      qa.push({ question, choices, correctIndex });
    } else {
      i++;
    }
  }
  return qa;
}

function parsePairs(input) {
  return input
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean)
    .map(l => {
      const m = l.split(/\s*(->|=|:|\t)\s*/);
      return m.length >= 3
        ? { left: m[0].trim(), right: m[2].trim() }
        : { left: l, right: "" };
    });
}

const Ca4thEducatorMath = () => {
  const navigate = useNavigate();
  const [popupContent, setPopupContent] = useState(null);
  const [visibleTopics, setVisibleTopics] = useState(() => {
    const savedVisibility = localStorage.getItem("visibleTopicsMath");
    return savedVisibility ? JSON.parse(savedVisibility) : Array(27).fill(true);
  });

  const [form, setForm] = useState({
    sirName: "", firstName: "", lastName: "", grade: "", state: "", email: "",
    subject: "", style: "", facts: "", qa: ""
  });

  const [formVisible, setFormVisible] = useState(false);
  const [educatorControlsVisible, setEducatorControlsVisible] = useState(false);
  const [students, setStudents] = useState([]);
  const [user, setUser] = useState(null);
  const [progressData, setProgressData] = useState({});
  const auth = getAuth();
  const db = getFirestore();
  const subject = ["science", "socialstudies", "math", "other"];
  const PAYMENT_LINK_URL = "https://buy.stripe.com/4gM28q3OLd65eUB2Voebu0n";

  // ---- Smart form state ----
const [step, setStep] = useState(1);            // 1: Basics, 2: Content, 3: Review
const [factsRaw, setFactsRaw] = useState("");
const [qaRaw, setQaRaw] = useState("");
const [pairsRaw, setPairsRaw] = useState("");

// normalized content
const [factsList, setFactsList] = useState([]);
const [qaList, setQaList] = useState([]);
const [pairsList, setPairsList] = useState([]);

useEffect(() => {
  if (form.facts && !factsRaw) setFactsRaw(form.facts);
  if (form.qa && !qaRaw) setQaRaw(form.qa);
}, [form.facts, form.qa]); 

useEffect(() => {
  if (!user) return;
  setForm(f => ({
    ...f,
    sirName: user.sirName || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    grade: user.grade || "",
    state: user.state || "",
    email: user.email || ""
  }));
}, [user]);

const handlePayNow = async () => {
  const required = ["sirName","firstName","lastName","grade","state","email","subject","style"];
  const missing = required.filter(k => !String(form[k]||"").trim());

  // ensure we parse latest text before submit
  const parsedFacts = parseFacts(factsRaw);
  const parsedQA = parseQA(qaRaw);
  const parsedPairs = parsePairs(pairsRaw);

  if (parsedFacts.length === 0) missing.push("facts");
  if (parsedQA.length === 0) missing.push("qa");

  if (missing.length) { 
    alert("Please complete all required fields before continuing.");
    setStep( missing.includes("facts") || missing.includes("qa") ? 2 : 1 );
    return; 
  }

  const uid = getAuth().currentUser?.uid;
  if (!uid) { alert("Please sign in again."); return; }

  try {
    const docRef = await addDoc(collection(db, "customContent"), {
      userId: uid,
      user: {
        sirName: form.sirName, firstName: form.firstName, lastName: form.lastName,
        grade: form.grade, state: form.state, email: form.email
      },
      meta: {
        subject: form.subject,
        style: form.style,
        status: "submitted",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      content: {
        facts: parsedFacts,
        qa: parsedQA,
        dragDropPairs: parsedPairs,
        raw: { factsRaw, qaRaw, pairsRaw } // keep original paste for reference
      },
      paid: false
    });

    sessionStorage.setItem("customContentDocId", docRef.id);
    window.location.href = "https://buy.stripe.com/4gM28q3OLd65eUB2Voebu0n";
  } catch (e) {
    console.error("Error pre-saving customContent:", e);
    alert("Could not save your request. Please try again.");
  }
};

const handleFactsBlur = () => setFactsList(parseFacts(factsRaw));
const handleQaBlur = () => setQaList(parseQA(qaRaw));
const handlePairsBlur = () => setPairsList(parsePairs(pairsRaw));

const handleFormChange = (e) => {
  const { name, value } = e.target;
  setForm(prev => ({ ...prev, [name]: value }));
};

  
  const brightStar = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbright_star.png?alt=media&token=b033c5b7-788e-401c-b650-98399c1e8551";
  const blankStar = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fblank_star.png?alt=media&token=0dd0e339-3016-4f1c-8db2-eae91a16c21a";

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          setUser({ ...userSnap.data(), uid: currentUser.uid });
        } else {
          console.error("No such user found in Firestore!");
        }
      } else {
        console.error("No user is signed in.");
      }
    });

    return () => unsubscribe();
  }, [auth, db]);

  useEffect(() => {
    if (!user?.uid) return; // Ensure user UID exists before making a request
  
    const fetchEducatorSettings = async () => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userDocRef);
  
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // Ensure visibility settings are retrieved from Firestore
          const storedVisibility = userData.scitools?.map(tool => tool.visible) || Array(27).fill(true);
          setVisibleTopics(storedVisibility);
  
          // Sync with localStorage for persistence across sessions
          localStorage.setItem("visibleTopicsMath", JSON.stringify(storedVisibility));
        } else {
          console.error("Educator settings not found!");
        }
      } catch (error) {
        console.error("Error fetching educator settings:", error);
      }
    };
  
    fetchEducatorSettings();
  }, [user]); // Runs when the user state updates

  const saveProgressToUserData = async (user, subject, slug, field) => {
    if (!user?.uid || !slug || !field || typeof subject !== "string") return;

    const userDataRef = doc(db, "userData", user.uid);
    const subjectKey = `${user.state.toLowerCase()}_${user.grade.toLowerCase()}_${subject.toLowerCase()}`; // Fixed template literal

    await setDoc(
      userDataRef,
      {
        [subjectKey]: {
          [slug]: {
            [field]: true,
          },
        },
      },
      { merge: true }
    );

    // When setting user progress, do this once if not stored already
    await setDoc(
      userDataRef,
      {
        sirName: user.sirName,
        firstName: user.firstName,
        lastName: user.lastName,
        state: user.state,
        city: user.city,
        zip: user.zip,
      },
      { merge: true }
    );
  };

  const fetchProgress = async () => {
    if (!user?.uid) return;

    try {
      const subjectKey = `${user.state.toLowerCase()}_${user.grade.toLowerCase()}_math`; // Fixed template literal
      const userDataRef = doc(db, "userData", user.uid);
      const userDataSnap = await getDoc(userDataRef);
      const userData = userDataSnap.data() || {};

      const videoMusicData = userData[subjectKey] || {};

      const progressMap = {};
      topics.forEach((topic) => {
        const slug = topic.slug;
        if (!slug) return;

        const vmEntry = videoMusicData[slug] || {};

        const gameValue = vmEntry?.game;
        const isGameComplete = typeof gameValue === "boolean"
          ? gameValue
          : Boolean(gameValue?.completed);

        const score = typeof gameValue === "object" && gameValue?.score
          ? gameValue.score
          : 0;

        progressMap[slug] = {
          video: Boolean(vmEntry?.video),
          music: Boolean(vmEntry?.music),
          game: isGameComplete,
          score,
        };
      });

      setProgressData(progressMap);
    } catch (error) {
      console.error("Error fetching user progress:", error);
    }
  };

  useEffect(() => {
    fetchProgress();
  }, [user]);
  
  const handleCheckboxChange = (index) => {
    const updatedVisibleTopics = [...visibleTopics];
    updatedVisibleTopics[index] = !updatedVisibleTopics[index];
    setVisibleTopics(updatedVisibleTopics);
    localStorage.setItem("visibleTopicsMath", JSON.stringify(updatedVisibleTopics));
  };

  const handleSaveChanges = async () => {
    if (!user || !user.uid) {
      console.error("User UID is not defined.");
      return;
    }
  
    if (!topics || !Array.isArray(topics)) {
      console.error("Topics is not defined or not an array.");
      return;
    }
  
    const updatedTools = topics.map((topic, index) => ({
          title: topic.title,
          slug: topic.slug,             // ✅ save slug for reliable matching
          standardNumber: topic.standardNumber || "",
          subject: "math",           // (optional) helps if you store per-subject
          visible: visibleTopics[index] ?? true,
        }));
  
    try {
      const userDocRef = doc(db, "users", user.uid); // Ensure user.uid is defined
      await updateDoc(userDocRef, { scitools: updatedTools });
      alert("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };
  

  const handleOpenPopup = (content) => {
    setPopupContent(content);
  };

  const handleClosePopup = () => {
    setPopupContent(null);
  };

  const handleHomeClick = () => {
    if (user) {
      const role = user.role.toLowerCase();
      if (role.includes("student")) {
        navigate("/dashboard");
      } else if (role.includes("educator")) {
        navigate("/educator/dashboard");
      } else if (role.includes("admin") || role.includes("administrator")) {
        navigate("/admin");
      } else {
        console.error("Unknown user role:", role);
      }
    } else {
      console.error("User is not defined.");
    }
  };

  const handleSubjectClick = (subject) => {
    const role = "ca-4th-educator"; // Replace with actual role logic
    const path = subject === "math" 
      ? `/educator/ca/ca-4th-educator-math` 
      : `/educator/ca/ca-4th-educator-${subject}`;
    navigate(path);
  };

  const handleRequestClick = () => {
    setFormVisible(true);
  };

  const handleFormClose = () => {
    setFormVisible(false);
  };

  const handleEducatorControlsClick = () => {
    setEducatorControlsVisible(true);
  };

  const handleEducatorControlsClose = () => {
    setEducatorControlsVisible(false);
  };

  const handleStudentProgressClick = async () => {
    if (!user || !user.educatorCode) {
      console.error("No logged-in user or educator code found.");
      return;
    }
  
    try {
      const q = query(
        collection(db, "users"),
        where("role", "==", "ca-4th-student"),
        where("educatorCode", "==", user.educatorCode)
      );
  
      const querySnapshot = await getDocs(q);
      const matchingUsers = querySnapshot.docs.map(doc => doc.data());
  
      setPopupContent(
        <div>
          <h2 style={{ color: "black" }}>Student Progress</h2>
          {user && (
            <>
              <h2 style={{ color: "black" }}>{user.sirName} {user.firstName} {user.lastName}'s Class</h2>
              <h3>Educator Code: {user.educatorCode}</h3>
            </>
          )}
          <h2>Student List</h2>
          <div style={styles.studentButtons}>
            {matchingUsers.length === 0 ? (
              <p>No students found with the specified criteria.</p>
            ) : (
              matchingUsers.map((student, index) => (
                <button
                  key={index}
                  style={{
                    ...styles.studentButton,
                    backgroundColor: `hsl(${(index * 40) % 360}, 70%, 80%)`,
                  }}
                  onClick={() => handleStudentClick(student.firstName)}
                >
                  {student.firstName}
                </button>
              ))
            )}
          </div>
        </div>
      );
    } catch (error) {
      console.error("Error fetching students:", error);
      setPopupContent(<p>Error loading students.</p>);
    }
  };

  const handleStudentClick = (firstName) => {
    alert(`It's me ${firstName}!`);
  };

  const handleGameClick = (gameUrl) => {
    navigate(gameUrl);
  };
  
  // Background image URL (set this to null or empty string to use the default color)
  const backgroundImage = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbg-texture-light-pink.png?alt=media&token=8b36c5d9-b7d2-4788-8ea4-19f7081bb7b3"; // Replace with actual background image URL
// Extract all unique standardNumbers from topics in order of appearance
const uniqueStandardNumbers = Array.from(
  new Set(topics.map(topic => topic.standardNumber))
);

// Map each unique standardNumber to a color based on position
const standardNumberColorMap = {};
uniqueStandardNumbers.forEach((number, index) => {
  const colorIndex = index % styles.topicColors.length;
  standardNumberColorMap[number] = styles.topicColors[colorIndex];
});

return (
  <div
    style={{
      ...styles.container,
      backgroundImage: backgroundImage
        ? `url(${backgroundImage})`
        : undefined,
      background: backgroundImage
        ? undefined
        : "linear-gradient(to bottom, #6dd5fa, #2980b9)", // Attractive red to pink gradient
    }}
  >
    <img
      src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
      alt="Logo"
      style={styles.logo}
    />
    <div style={styles.navButtons}>
      <button
        onClick={handleHomeClick}
        style={styles.homeButton} // Purple color
      >
        Home
      </button>
      <button
        onClick={() => handleSubjectClick("science")}
        style={styles.scienceButton} // Blue color
      >
        Science
      </button>
      <button
        onClick={() => handleSubjectClick("socialstudies")}
        style={styles.socialStudiesButton} // Green color
      >
        Social Studies
      </button>
      <button
        onClick={() => handleSubjectClick("math")}
        style={styles.mathButton} // Reddish color
      >
        Math
      </button>
      <button
        onClick={() => handleSubjectClick("other")}
        style={styles.otherButton} // Yellowish color
      >
        Other
      </button>
    </div>
    <h1 style={{ textAlign: "center", marginBottom: "20px" }}>
{user && user.firstName && user.lastName ? (
  <>
    <span style={{
      fontSize: "22px",
      display: "inline-block",
      marginBottom: "10px",
      padding: "5px 15px",
      backgroundColor: "#fef3c7", // light yellow
      color: "#b45309",
      fontWeight: "600",
      borderRadius: "6px",
      textTransform: "uppercase",
      letterSpacing: "1px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    }}>
      {user.sirName} {user.lastName}'s Class
    </span>
    <br />
    <span style={{
      fontSize: "40px",
      fontWeight: "bold",
      color: "#0a3d62",
      textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
      display: "block",
      marginBottom: "12px",
      marginTop: "10px"
    }}>
     California 4th Grade Math
    </span>
  </>
) : (
  <span style={{
    fontSize: "32px",
    fontWeight: "bold",
    color: "#0a3d62",
    textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
    display: "block",
    marginBottom: "12px"
  }}>
    California 4th Grade Math Educator Portal
  </span>
)}
</h1>

<p style={{
fontSize: "18px",
color: "#333",
textAlign: "center",
lineHeight: "1.6",
marginBottom: "40px",
padding: "0 15px"
}}>
Watch the video or listen to the song to explore new concepts,<br />
then play the game to reinforce learning in a fun way!<br /><br />
Educators can request custom materials, adjust content settings,<br />
and track student progress below.
</p>
 
   {/* Topics */}
   <div style={styles.topicsContainer}>
   {topics.map((topic, index) =>
visibleTopics[index] && topic.title?.trim() ? (
  <div key={index} style={{ 
    ...styles.topicCard, 
    borderColor: standardNumberColorMap[topic.standardNumber] || "#ccc" 
  }}>
    <div style={styles.topicContent}>
      {/* Completion Stars */}
      <div style={{ display: "flex", justifyContent: "center", gap: "5px", marginBottom: "5px" }}>
        {/* Video Star */}
        <img
          title={progressData[topic.slug]?.video ? "✅ Video Completed" : "⏳ Video Not Started"}
          src={progressData[topic.slug]?.video ? brightStar : blankStar}
          alt="Video Star"
          style={{ width: 20, height: 20, cursor: "help" }}
        />
        {/* Music Star */}
        <img
          title={progressData[topic.slug]?.music ? "✅ Music Completed" : "⏳ Music Not Started"}
          src={progressData[topic.slug]?.music ? brightStar : blankStar}
          alt="Music Star"
          style={{ width: 20, height: 20 }}
        />
        {/* Game Star */}
        <img
          title={
            progressData[topic.slug]?.game
              ? `✅ Game Completed (Score: ${progressData[topic.slug]?.score || 0})`
              : "⏳ Game Not Started"
          }
          src={progressData[topic.slug]?.game ? brightStar : blankStar}
          alt="Game Star"
          style={{ width: 20, height: 20 }}
        />
      </div>
      {topic.titleImage ? (
        <>
          <img src={topic.titleImage} alt={topic.title} style={styles.titleImage} />
          <h3 style={styles.topicTitle}>{topic.title}</h3>
        </>
      ) : (
        <h3 style={styles.topicTitle}>{topic.title}</h3>
      )}

      {topic.subtitle1 && <p style={styles.subtitle1}>{topic.subtitle1}</p>}
      <p style={styles.subtitle2}>Topic: {index + 1}</p>   
      <p style={styles.topicDescription}>
        <span style={styles.standardNumber}>{topic.standardNumber}</span> {topic.description}
      </p>
    </div>

    <div style={styles.topicButtons}>
      {topic.video && (
        <button
          style={styles.button}
          onClick={() =>
            handleOpenPopup(
              <VideoTool
                videoUrl={topic.video}
                subjectKey={`${user.state.toLowerCase()}_${user.grade.toLowerCase()}_math`} // Use "math" directly
                slug={topic.slug}
                onProgressUpdate={fetchProgress}
              />
            )
          }
        >
          <img
            src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fvideo-topic-image-button2.png?alt=media&token=c787b8ef-9cfa-4051-ae09-9d58cee36987"
            alt="Video"
            style={{ width: "20px", height: "20px", marginRight: "8px" }}
          />
          Video
        </button>
      )}
      {topic.music && (
<button
  style={styles.button}
  onClick={() =>
    handleOpenPopup(
      <div>
        <MusicTool
          musicUrl={topic.music}
          subjectKey={`${user.state.toLowerCase()}_${user.grade.toLowerCase()}_math`} // Use "math" directly
          slug={topic.slug}
          onProgressUpdate={fetchProgress}
        />
        <div style={styles.lyricsContainer}>
          {topic.lyrics.map((line, idx) => (
            <p key={idx} style={styles.lyricLine}>{line}</p>
          ))}
        </div>
      </div>
    )
  }
>
  🎵 Music
</button>
)}
      {topic.slug && topic.gameType && (
        <button
          style={styles.button}
          onClick={() => handleGameClick(`/treasure-map/math/${topic.slug}`)}
        >
          🎮 Game
        </button>
      )}
    </div>
  </div>
) : null
)}

    </div>

    {popupContent && (
      <div style={styles.popupOverlay}>
        <div style={styles.popupBox}>
          <button onClick={handleClosePopup} style={styles.closeButton}>
            ✖
          </button>
          {popupContent}
        </div>
      </div>
    )}
    
    {/* Educator Controls Container */}
    {educatorControlsVisible && (
      <div style={styles.popupOverlay}>
        <div style={styles.popupBox}>
          <button onClick={handleEducatorControlsClose} style={styles.closeButton}>
            ✖
          </button>
          <div style={styles.educatorControls}>
            <h2>Educator Controls</h2>
            <h3>Check all tools you want your student to see. Uncheck those you want to hide.</h3>
            <div style={styles.controlsGrid}>
              {topics.map((topic, index) => (
                <div key={index} style={styles.controlItem}>
                  <label>
                    <input
                      type="checkbox"
                      checked={visibleTopics[index]}
                      onChange={() => handleCheckboxChange(index)}
                    />
                    {topic.title}
                  </label>
                </div>
              ))}
            </div>
            <button onClick={handleSaveChanges} style={styles.saveButton}>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Buttons Container */}
    <div style={styles.buttonsContainer}>
      <button style={styles.educatorControlsButton} onClick={handleEducatorControlsClick}>
        Click Here for Educator Controls
      </button>
      <button style={styles.requestButton} onClick={handleRequestClick}>
        Click Here to Request Custom Material
      </button>
      <button style={styles.studentProgressButton} onClick={handleStudentProgressClick}>
        Click Here for Student Progress
      </button>
    </div>

    {/* FORM CONTAINER */}
    {formVisible && (
<div style={styles.popupOverlay}>
  <div style={{ ...styles.popupBox, maxWidth: "900px" }}>
    <button onClick={handleFormClose} style={styles.closeButton}>✖</button>

    <div style={styles.formContainer}>
      <h2 style={{marginTop:0}}>Custom Material Request</h2>

      {/* Stepper */}
      <div style={styles.stepper}>
        <div style={{...styles.step, ...(step===1?styles.stepActive:{} )}}>1. Basics</div>
        <div style={{...styles.step, ...(step===2?styles.stepActive:{} )}}>2. Content</div>
        <div style={{...styles.step, ...(step===3?styles.stepActive:{} )}}>3. Review</div>
      </div>

      {/* STEP 1: Basics */}
      {step === 1 && (
        <form onSubmit={(e)=>e.preventDefault()}>
          <div style={styles.formRow}>
            {/* Locked (auto-filled) */}
            <div style={styles.formField}>
              <label>Sir Name</label>
              <select name="sirName" value={form.sirName} disabled style={styles.input} onChange={handleFormChange}>
                <option>Mr.</option><option>Mrs.</option><option>Ms.</option><option>Miss</option><option>N/A</option>
              </select>
            </div>
            <div style={styles.formField}>
              <label>First Name</label>
              <input name="firstName" value={form.firstName} disabled style={styles.input} onChange={handleFormChange}/>
            </div>
            <div style={styles.formField}>
              <label>Last Name</label>
              <input name="lastName" value={form.lastName} disabled style={styles.input} onChange={handleFormChange}/>
            </div>
            <div style={styles.formField}>
              <label>Grade</label>
              <select name="grade" value={form.grade} disabled style={styles.input} onChange={handleFormChange}>
                <option>1st</option><option>2nd</option><option>3rd</option><option>4th</option><option>5th</option>
              </select>
            </div>
            <div style={styles.formField}>
              <label>State</label>
              <select name="state" value={form.state} disabled style={styles.input} onChange={handleFormChange}>
                <option>AL</option><option>AK</option><option>AZ</option><option>AR</option><option>CA</option>
                <option>CO</option><option>CT</option><option>DE</option><option>DC</option><option>FL</option>
                <option>GA</option><option>HI</option><option>ID</option><option>IL</option><option>IN</option>
                <option>IA</option><option>KS</option><option>KY</option><option>LA</option><option>ME</option>
                <option>MD</option><option>MA</option><option>MI</option><option>MN</option><option>MS</option>
                <option>MO</option><option>MT</option><option>NE</option><option>NV</option><option>NH</option>
                <option>NJ</option><option>NM</option><option>NY</option><option>NC</option><option>ND</option>
                <option>OH</option><option>OK</option><option>OR</option><option>PA</option><option>RI</option>
                <option>SC</option><option>SD</option><option>TN</option><option>TX</option><option>UT</option>
                <option>VT</option><option>VA</option><option>WA</option><option>WV</option><option>WI</option><option>WY</option>
              </select>
            </div>
            <div style={styles.formField}>
              <label>Email</label>
              <input type="email" name="email" value={form.email} disabled style={styles.input} onChange={handleFormChange}/>
            </div>

            {/* Editable */}
            <div style={styles.formField}>
              <label>Subject</label>
              <select name="subject" value={form.subject} style={styles.input} onChange={handleFormChange} required>
                <option value="">Select Subject</option>
                <option>Math</option><option>Science</option><option>Social Studies</option><option>Other</option>
              </select>
            </div>
            <div style={styles.formField}>
              <label>Style</label>
              <select name="style" value={form.style} style={styles.input} onChange={handleFormChange}>
                <option value="">Choose Style</option>
                <option>Any Style</option><option>Light Rock</option><option>Hip Hop</option>
                <option>Country</option><option>Latin</option><option>R&B</option>
              </select>
            </div>
          </div>

          <div style={styles.wizardActions}>
            <button type="button" onClick={()=>setStep(2)} style={styles.nextButton}>Next</button>
            <button type="button" onClick={handleFormClose} style={styles.cancelButton}>Cancel</button>
          </div>
        </form>
      )}

      {/* STEP 2: Content (smart editors + previews) */}
      {step === 2 && (
        <>
          {/* Facts */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={{margin:0}}>Key Facts</h3>
              <span style={styles.countBadge}>{factsList.length} items</span>
            </div>
            <div style={styles.twoCol}>
              <textarea
                value={factsRaw}
                onChange={(e)=>setFactsRaw(e.target.value)}
                onBlur={handleFactsBlur}
                placeholder="Paste or type facts. One per line. We'll format it."
                style={styles.bigTextarea}
              />
              <div style={styles.previewBox}>
                <p style={styles.previewTitle}>Preview</p>
                <ul style={{margin:0, paddingLeft:"18px"}}>
                  {factsList.map((f, idx)=> <li key={idx}>{f}</li>)}
                </ul>
              </div>
            </div>
          </div>

          {/* Q&A */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={{margin:0}}>Game Questions</h3>
              <span style={styles.countBadge}>{qaList.length} questions</span>
            </div>
            <div style={styles.twoCol}>
              <textarea
                value={qaRaw}
                onChange={(e)=>setQaRaw(e.target.value)}
                onBlur={handleQaBlur}
                placeholder={`Just type. We'll detect questions (lines with ? or starting with who/what/when/where/why/how).
Answers go on the next lines. Mark correct with *, ✓, or (correct).`}
                style={styles.bigTextarea}
              />
              <div style={styles.previewBox}>
                <p style={styles.previewTitle}>Preview</p>
                <ol style={{margin:0, paddingLeft:"18px"}}>
                  {qaList.map((q, i)=>(
                    <li key={i} style={{marginBottom:"8px"}}>
                      <div><strong>{q.question}</strong></div>
                      <ul style={{margin:"4px 0 0 0", paddingLeft:"18px"}}>
                        {q.choices.map((c, idx)=>(
                          <li key={idx}>
                            {c}{idx===q.correctIndex ? "  ✅" : ""}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>

          {/* Drag & Drop pairs */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={{margin:0}}>Drag &amp; Drop Pairs (optional)</h3>
              <span style={styles.countBadge}>{pairsList.length} pairs</span>
            </div>
            <div style={styles.twoCol}>
              <textarea
                value={pairsRaw}
                onChange={(e)=>setPairsRaw(e.target.value)}
                onBlur={handlePairsBlur}
                placeholder={`Type pairs like:
Term -> Definition
or "Term = Definition", "Term : Definition", or use a tab between them.`}
                style={styles.bigTextarea}
              />
              <div style={styles.previewBox}>
                <p style={styles.previewTitle}>Preview</p>
                <div>
                  {pairsList.map((p, idx)=>(
                    <div key={idx} style={styles.pairRow}>
                      <div style={styles.pairLeft}>{p.left}</div>
                      <div style={styles.pairArrow}>⇄</div>
                      <div style={styles.pairRight}>{p.right}</div>
                    </div>
                  ))}
                  {pairsList.length===0 && <p style={{opacity:.7}}>No pairs yet.</p>}
                </div>
              </div>
            </div>
          </div>

          <div style={styles.wizardActions}>
            <button type="button" onClick={()=>setStep(1)} style={styles.secondaryButton}>Back</button>
            <button type="button" onClick={()=>setStep(3)} style={styles.nextButton}>Review</button>
          </div>
        </>
      )}

      {/* STEP 3: Review & Pay */}
      {step === 3 && (
        <>
          <div style={styles.reviewGrid}>
            <div style={styles.reviewCard}>
              <h4 style={{marginTop:0}}>Basics</h4>
              <p><strong>Subject:</strong> {form.subject || "—"}</p>
              <p><strong>Style:</strong> {form.style || "—"}</p>
              <button onClick={()=>setStep(1)} style={styles.smallEditBtn}>Edit</button>
            </div>

            <div style={styles.reviewCard}>
              <h4 style={{marginTop:0}}>Facts</h4>
              <p>{factsList.length} items</p>
              <button onClick={()=>setStep(2)} style={styles.smallEditBtn}>Edit</button>
            </div>

            <div style={styles.reviewCard}>
              <h4 style={{marginTop:0}}>Game Questions</h4>
              <p>{qaList.length} questions</p>
              <button onClick={()=>setStep(2)} style={styles.smallEditBtn}>Edit</button>
            </div>

            <div style={styles.reviewCard}>
              <h4 style={{marginTop:0}}>Drag &amp; Drop</h4>
              <p>{pairsList.length} pairs</p>
              <button onClick={()=>setStep(2)} style={styles.smallEditBtn}>Edit</button>
            </div>
          </div>

          <p style={{marginTop:20}}>
            Please allow 7–14 days for the material to be added to your portal. All custom
            material is protected under the Terms of Use. Payment allows use within your portal
            until canceled.
          </p>

          <div style={styles.wizardActions}>
            <button type="button" onClick={()=>setStep(2)} style={styles.secondaryButton}>Back</button>
            <button type="button" onClick={handlePayNow} style={styles.payButton}>Pay Now</button>
            <button type="button" onClick={handleFormClose} style={styles.cancelButton}>Cancel</button>
          </div>
        </>
      )}
    </div>
  </div>
</div>
)}

<div style={styles.footer}>
<footer style={{
  fontSize: "22px",
  display: "inline-block",
  marginBottom: "10px",
  padding: "5px 15px",
  backgroundColor: "#fef3c7", // light yellow
  color: "#b45309",
  fontWeight: "600",
  borderRadius: "6px",
  textTransform: "uppercase",
  letterSpacing: "1px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  textAlign: "center"
}}>
  <p style={{ margin: 0 }}>© Smart Board Learning™</p>
  <p style={{ margin: 0 }}>All rights reserved.</p>
</footer>
    </div>
    </div>
  );
};

const baseNavButton = {
  color: "#fff",
  padding: "10px 20px",
  fontSize: "25px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  width: "200px",
  height: "200px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)", // Layered shadow
  border: "2px solid #222", // Border for depth
  transition: "transform 0.2s, box-shadow 0.2s",
};

const styles = {
  container: {
    textAlign: "center",
    fontFamily: "Arial, sans-serif",
    minHeight: "100vh", // Ensure the background covers the full viewport height
    padding: "20px",
    backgroundSize: "cover", // Ensure the background image or gradient covers the entire container
    backgroundPosition: "center",
    backgroundColor: "#f0f4f8", // Softer background color
    background: "radial-gradient(circle at 30% 30%, #d0eaff, #2980b9)", // Updated gradient
  },
  footer: {
    marginTop: "40px",
    fontSize: "20px",
    color: "#000",
    textAlign: "center",
  },
  logo: {
    width: "150px",
    margin: "0 auto",
    display: "block",
    transform: "rotate(-2deg)", // Slight tilt
    filter: "drop-shadow(2px 4px 2px rgba(0,0,0,0.4))", // Drop shadow for bounce effect
  },
  navButtons: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
    gap: "10px",
  },
  navButton: baseNavButton, // Base style for nav buttons
  homeButton: {
    ...baseNavButton,
    backgroundColor: "#A020F0", // Purple color
  },
  scienceButton: {
    ...baseNavButton,
    backgroundColor: "#4a90e2", // Blue color
  },
  socialStudiesButton: {
    ...baseNavButton,
    backgroundColor: "#4CAF50", // Green color
  },
  mathButton: {
    ...baseNavButton,
    backgroundColor: "#FF6347", // Reddish color
  },
  otherButton: {
    ...baseNavButton,
    backgroundColor: "#FFD700", // Yellowish color
  },
  title: {
    fontSize: "35px",
    color: "#000", // Change color to black
    marginTop: "20px",
  },
  subtitle: {
    fontSize: "20px",
    color: "#555", // Softer subtitle color
    marginBottom: "20px",
  },
  topicsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
    padding: "20px",
  },
  topicCard: {
    backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent white background
    borderRadius: "10px",
    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)", // Layered shadow for depth
    padding: "20px",
    textAlign: "center",
    backgroundImage: `
      linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), 
      url('https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fsocialstudies-bg-texture-topic.png?alt=media&token=3d079085-acc9-402b-b3f2-a8019b456121')
    `, // Add faded overlay
    backgroundSize: "cover",
    backgroundBlendMode: "overlay", // Blend the gradient and image
    border: "5px solid", // Add border
  },
  topicTitle: {
    fontSize: "22px",
    color: "#333",
    marginBottom: "10px",
    fontFamily: "'Quicksand', sans-serif", // Playful font without cursive
  },
  topicContent: {
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  titleImage: {
    width: "100%",
    maxHeight: "150px",
    objectFit: "contain",
    marginBottom: "10px",
  },
  subtitle1: {
    fontSize: "18px",
    color: "#777", // Softer subtitle color
    fontStyle: "italic",
  },
  subtitle2: {
    fontSize: "14px",
    color: "#555",
  },
  topicDescription: {
    fontSize: "14px",
    color: "#555", // Softer description color
    textAlign: "center",
  },
  topicButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  },
  button: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px",
    fontSize: "14px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    flex: "1",
    margin: "0 5px",
    boxShadow: "0 4px 0 #1e5aa8, 0 6px 10px rgba(0,0,0,0.25)", // Blue shadow
    border: "2px solid #1e5aa8", // Blue border
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  ":hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 12px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.2)", // Enhanced shadow on hover
  },
  ":active": {
    transform: "translateY(0)",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)", // Subtle shadow on click
  },
  popupOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  popupBox: {
    // Without this a long lyric sheet pushes the box off-screen. Content
    // scrolls inside the popup now; the popup itself never exceeds the
    // viewport.
    maxHeight: "90vh",
    overflowY: "auto",
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Softer shadow
    position: "relative",
    maxWidth: "960px",
    width: "95%",
  },
  closeButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: "#ff4d4d",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    fontSize: "18px",
    cursor: "pointer",
    textAlign: "center",
    lineHeight: "30px",
  },
  // A fixed 16:9 stage. Every video renders in an identically sized frame
  // regardless of its source dimensions — a portrait clip gets black bars on
  // the sides instead of stretching the popup taller than the screen, which
  // is what forced people to scroll.
  mediaStage: {
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#000",
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "10px",
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    border: "none",
    background: "#000",
  },
  // Audio has no intrinsic height, so it keeps a simple full-width rule.
  mediaAudio: {
    width: "100%",
    marginTop: "10px",
  },
  lyricsContainer: {
    marginTop: "20px",
    textAlign: "center",
    maxHeight: "500px", // Set a maximum height
    overflowY: "auto", // Enable vertical scrolling
  },
  lyricLine: {
    fontSize: "16px",
    color: "#333",
    margin: "5px 0",
  },
  educatorControls: {
    backgroundColor: "#e0e7ef", // Softer background color for educator controls
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
  },
  controlsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "10px",
  },
  controlItem: {
    textAlign: "left",
  },
  formContainer: {
    backgroundColor: "#f9f9f9",
    padding: "30px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 2)",
    maxHeight: "80vh", // Limit height for smaller screens
    maxWidth: "100%", // Ensure it fits within the screen width
    overflow: "auto", // Enable vertical and horizontal scrolling
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "20px 30px",
    alignItems: "flex-start",
  },

  formField: {
    flex: "1 1 320px",     // bigger base width but still flexible
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch", // so inputs expand to full column width
  },

  input: {
  width: "100%",
  minWidth: 0,           // IMPORTANT in flex layouts to prevent clipping
  boxSizing: "border-box",
  padding: "10px",
  borderRadius: "5px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  border: "1px solid #ccc",
  marginTop: "5px",
},
textarea: {
  width: "100%",
  minWidth: 0,           // same reason as above
  boxSizing: "border-box",
  padding: "10px",
  borderRadius: "5px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  border: "1px solid #ccc",
  marginTop: "10px",
  minHeight: "140px",
},

  payButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
  },
  requestButton: {
    backgroundColor: "#ffa500", // Orange color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #cc7000, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #b86e00", // Border for depth
    marginTop: "20px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "#ff4d4d",
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
    marginLeft: "10px",
  },
  educatorControlsButton: {
    backgroundColor: "#A020F0", // Purple color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #5e1280, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #4a0e60", // Border for depth
    marginTop: "20px",
    marginLeft: "10px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    marginTop: "20px",
  },
  studentProgressButton: {
    backgroundColor: "#ff69b4", // Pink color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #b4004b, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #8e0038", // Border for depth
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "20px", // Add marginTop to align the button
  },
  studentProgress: {
    backgroundColor: "#f9f9f9",
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  studentButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "center",
  },
  studentButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#000",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
  },
  saveButton: {
    backgroundColor: "linear-gradient(to bottom, #4caf50, #388e3c)", // Match button style
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 4px 0 #2e7d32, 0 6px 10px rgba(0,0,0,0.25)", // Layered shadow
    border: "2px solid #1b5e20", // Border for depth
    marginTop: "20px",
  },
  
  // ---- Wizard/stepper + cards ----
stepper: {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "8px",
  marginBottom: "16px"
},
step: {
  background: "#f1f5f9",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  padding: "8px 10px",
  textAlign: "center",
  fontWeight: 600
},
stepActive: {
  background: "#dbeafe",
  borderColor: "#60a5fa",
  color: "#1d4ed8"
},
wizardActions: {
  display: "flex",
  gap: "10px",
  justifyContent: "flex-end",
  marginTop: "16px"
},
nextButton: {
  backgroundColor: "#4a90e2",
  color: "#fff",
  padding: "10px 16px",
  fontSize: "16px",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  boxShadow: "0 2px 4px rgba(0,0,0,.2)"
},
secondaryButton: {
  backgroundColor: "#e5e7eb",
  color: "#111827",
  padding: "10px 16px",
  fontSize: "16px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  cursor: "pointer"
},
card: {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  boxShadow: "0 1px 2px rgba(0,0,0,.06)",
  padding: "16px",
  marginBottom: "16px"
},
cardHeader: {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "10px"
},
countBadge: {
  background: "#f3f4f6",
  border: "1px solid #e5e7eb",
  borderRadius: "999px",
  padding: "4px 10px",
  fontSize: "12px"
},
twoCol: {
  display: "grid",
  gap: "12px",
  gridTemplateColumns: "1fr 1fr"
},
bigTextarea: {
  width: "100%",
  minHeight: "160px",
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  padding: "10px",
  fontSize: "14px",
  resize: "vertical"
},
previewBox: {
  border: "1px dashed #d1d5db",
  borderRadius: "8px",
  padding: "10px",
  overflowY: "auto",
  maxHeight: "240px",
  background: "#fafafa"
},
previewTitle: {
  margin: "0 0 8px 0",
  fontSize: "12px",
  opacity: .7
},
pairRow: {
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "center",
  gap: "8px",
  marginBottom: "6px"
},
pairLeft: { padding: "6px 8px", borderRadius: "6px", background: "#eef2ff" },
pairRight: { padding: "6px 8px", borderRadius: "6px", background: "#ecfeff" },
pairArrow: { opacity: .6 },
reviewGrid: {
  display: "grid",
  gap: "12px",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  marginTop: "8px"
},
reviewCard: {
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  padding: "12px",
  background: "#fff"
},
smallEditBtn: {
  marginTop: "6px",
  background: "#f3f4f6",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  padding: "6px 10px",
  cursor: "pointer",
  fontSize: "12px"
},

  standardNumber: {
    fontWeight: "bold",
    color: "#222",
    marginRight: "5px",
  },
  topicColors: [
    "#FF6347",  // Reddish
    "#4CAF50",  // Green
    "#FFD700",  // Yellow
    "#4a90e2",  // Blue
    "#A020F0",  // Purple
    "#FF69B4",  // Pink
    "#FFA500",  // Orange
    "#2980b9",  // Deep Blue
    "#00CED1",  // Dark Turquoise (new)
    "#8B4513",  // Saddle Brown (new)
  ],
};


export default Ca4thEducatorMath;